import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import { useSchools } from "hooks/useSchools";
import { useAuth } from "contexts/AuthContext";
import { useStudentPhoto } from "hooks/useStudents";
import { ID_CARD_EXPORT_WIDTH_PX } from "utils/idCardExport";

export type IdCardPreviewData = {
  schoolId?: string | number;
  studentId?: string | number;
  /** Bulk: pre-fetched school logo file name; avoids N× /schools/{id}/logo-info */
  logoFileName?: string | null;
  /** Bulk: when true, do not call useSchoolLogoInfo */
  skipSchoolLogoFetch?: boolean;
  /** Bulk: pre-fetched student photo blob URL; avoids N× /students/{id}/studentPhoto */
  photoUrl?: string | null;
  /** Bulk: when true, do not call useStudentPhoto */
  skipStudentMediaFetch?: boolean;
  schoolName: string;
  district?: string;
  schoolTagline: string;
  schoolAddressLines: string[];
  phoneNo: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dob: string;
  contactNo: string;
  mobileNo?: string;
  address: string;
  className: string;
  section?: string;
};

function getInitials(name: string) {
  const parts = (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = (parts.length > 1 ? parts[parts.length - 1]?.[0] : parts[0]?.[1]) ?? "";
  return (first + second).toUpperCase() || "NA";
}

function IdCardPreview(props: { data: IdCardPreviewData }) {
  const { data } = props;
  const { user } = useAuth();
  const { useSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
  const schoolId = data.schoolId ?? (user as any)?.schoolId;
  const shouldFetchLogo = !!schoolId && !data?.logoFileName && !data?.skipSchoolLogoFetch;
  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(
    shouldFetchLogo ? (schoolId as any) : undefined
  );
  const schoolName = data.schoolName || "";

  const headerWaveSrc = useMemo(
    () =>
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        "<svg xmlns='http://www.w3.org/2000/svg' width='360' height='110' viewBox='0 0 360 110' preserveAspectRatio='none'>" +
          "<path d='M0,78 C72,106 130,98 180,80 C230,62 290,64 360,86 L360,110 L0,110 Z' fill='#ffffff'/>" +
          "<path d='M0,66 C78,94 136,88 180,74 C224,60 292,60 360,80' fill='none' stroke='rgba(165,205,255,0.95)' stroke-width='14' stroke-linecap='round' opacity='0.65'/>" +
          "<path d='M0,60 C82,90 140,84 180,70 C220,56 294,56 360,74' fill='none' stroke='rgba(214,234,255,1)' stroke-width='6' stroke-linecap='round' opacity='0.85'/>" +
          "</svg>"
      )}`,
    []
  );

  const studentId = useMemo(() => {
    const studentIdRaw = (data as any)?.studentId ?? (data as any)?.id;
    return typeof studentIdRaw === "string" ? Number(studentIdRaw) : (studentIdRaw as number | undefined);
  }, [data]);
  const shouldFetchPhoto = !!studentId && !data?.photoUrl && !data?.skipStudentMediaFetch;
  const studentIdForPhotoFetch = shouldFetchPhoto && Number.isFinite(studentId) ? studentId : undefined;
  const { imageUrl: authenticatedImageUrl, isLoading: photoLoading, error: photoError, hasPhoto } =
    useStudentPhoto(studentIdForPhotoFetch);

  return (
    <Box
      sx={{
        width: `${ID_CARD_EXPORT_WIDTH_PX}px`,
        maxWidth: "100%",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 14px 36px rgba(0,0,0,0.12)",
        border: "1px solid rgba(25,118,210,0.25)",
        bgcolor: "#ffffff",
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.2,
          pt: 2,
          pb: 5.6,
          color: "white",
          background: "linear-gradient(180deg, #1b63db 0%,rgba(15, 78, 196, 0.74) 100%)",
          position: "relative",
        }}
      >
        {/* Exact-style white wave */}
        <Box
          component="img"
          src={headerWaveSrc}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -1,
            height: 50,
            width: "100%",
            zIndex: 0,
            display: "block",
            objectFit: "fill",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", marginLeft: 2, marginBottom: 2 }}>
          {/* School Logo */}
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.95)",
              display: "grid",
              placeItems: "center",
              boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
              border: "2px solid rgba(255,255,255,0.65)",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            {(() => {
              const effectiveLogoFileName = data.logoFileName || (logoInfo as any)?.fileName || "";
              const shouldShowLoader = !data?.skipSchoolLogoFetch && logoLoading;

              // In bulk mode we never want a spinner captured in the PDF.
              if (shouldShowLoader) {
                return <CircularProgress size={22} sx={{ color: "#0f4fc4" }} />;
              }

              if (effectiveLogoFileName) {
                return (
                  <img
                    src={`${process.env.REACT_APP_API_URL}/schools/logos/${effectiveLogoFileName}`}
                    alt={`${schoolName} Logo`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                    onLoad={() => {}}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                       <div style="
                         color: white;
                         font-weight: bold;
                         font-size: 12px;
                         text-align: center;
                         line-height: 1.2;
                         display: flex;
                         align-items: center;
                         justify-content: center;
                         width: 100%;
                         height: 100%;
                         background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
                       ">
                         ${schoolName
                           .split(" ")
                           .map((word: string) => word.charAt(0))
                           .join("")
                           .substring(0, 3)}
       </div>
                     `;
                      }
                    }}
                  />
                );
              }

              // Fallback initials (bulk & non-bulk)
              return (
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "12px",
                    textAlign: "center",
                    lineHeight: 1.2,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  }}
                >
                  {schoolName
                    .split(" ")
                    .map((word: string) => word.charAt(0))
                    .join("")
                    .substring(0, 3)}
                </Typography>
              );
            })()}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                lineHeight: 1.05,
                textShadow: "0 2px 10px rgba(0,0,0,0.25)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box component="span" sx={{ fontSize: 24, fontWeight: 800 }}>
                {data.schoolName}
              </Box>
              {data.district ? (
                <Box component="span" sx={{ fontSize: 20, fontWeight: 400 }}>
                  {`, ${data.district}`}
                </Box>
              ) : null}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2.2, pb: 1.6, pt: 0.2, position: "relative" }}>
        {/* Photo */}
        <Box
          sx={{
            width: 152,
            height: 168,
            mx: "auto",
            mt: -5.8,
            borderRadius: 1,
            border: "3px solid #1b63db",
            boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
            bgcolor: "#ffffff",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(27,99,219,0.08) 0%, rgba(15,79,196,0.02) 60%, rgba(27,99,219,0.10) 100%)",
              display: "grid",
              placeItems: "center",
            }}
          >
            {photoLoading ? (
              <CircularProgress size={26} sx={{ color: "#1b63db" }} />
            ) : (data.photoUrl || (hasPhoto && authenticatedImageUrl)) && !photoError ? (
              <img
                src={(data.photoUrl || authenticatedImageUrl) ?? undefined}
                alt={`${data.studentName} Photo`}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  gap: 0.6,
                  color: "#1b63db",
                }}
              >
                <Person sx={{ fontSize: 44, color: "rgba(27,99,219,0.55)" }} />
                <Typography sx={{ fontWeight: 900, color: "#1b63db", letterSpacing: 1 }}>
                  {getInitials(data.studentName)}
                </Typography>
                <Typography sx={{ fontWeight: 800, color: "rgba(27,99,219,0.85)", fontSize: 12 }}>
                  NO PHOTO
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Student name */}
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 750,
            fontSize: 32,
            letterSpacing: 0.2,
            mt: 1.05,
            color: "#111827",
            position: "relative",
            zIndex: 1,
          }}
        >
          {data.studentName}
        </Typography>

        <Divider sx={{ my: 1.0, borderColor: "rgba(0,0,0,0.18)" }} />

        {/* Details table */}
        <Box sx={{ px: 0.2 }}>
          <Table
            size="small"
            sx={{
              "& td": {
                borderBottom: "1px solid rgba(0,0,0,0.14)",
                py: 0.55,
                fontSize: 14,
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: "44%", color: "#111827", fontWeight: 600 }}>
                  Father&apos;s Name
                </TableCell>
                <TableCell sx={{ color: "#111827" }}>{data.fatherName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: "#111827", fontWeight: 600 }}>
                  Mother&apos;s Name
                </TableCell>
                <TableCell sx={{ color: "#111827" }}>{data.motherName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: "#111827", fontWeight: 600 }}>
                  D.O.B.
                </TableCell>
                <TableCell sx={{ color: "#111827" }}>{data.dob}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: "#111827", fontWeight: 600 }}>
                  Contact No.
                </TableCell>
                <TableCell sx={{ color: "#111827" }}>{data.mobileNo}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: "#111827", fontWeight: 600 }}>
                  Address
                </TableCell>
                <TableCell sx={{ color: "#111827" }}>{data.address}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: 1.4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: 18, color: "#111827" }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Class :
            </Box>{" "}
            {data.section ? `${data.className} ${data.section}` : data.className}
          </Typography>

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ mt: -0.4, fontSize: 16, color: "#111827" ,marginTop: 4}}>
              Principal Sign.
            </Typography>
          </Box>
        </Box>

        {/* Subtle background curves */}
        <Box
          sx={{
            position: "absolute",
            left: -60,
            bottom: -60,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(27,99,219,0.08)",
            filter: "blur(0px)",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: -80,
            top: 180,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "rgba(27,99,219,0.06)",
            zIndex: 0,
          }}
        />
      </Box>
    </Box>
  );
}

const IdCardTemplate: React.FC<{ data: IdCardPreviewData; hidePrintButton?: boolean }> = ({
  data,
  // hidePrintButton: reserved for parity with other templates
}) => {
  return <IdCardPreview data={data} />;
};

export default IdCardTemplate;

